// The voice session, client side (BUILD 2 C2-4, WP2-8 step 3): one server
// session for the conversation, with `commit` per turn.
//
// - Frames are sent only for an open hands-free turn. `seq` gaps between turns
//   are allowed; the server
//   never asks for a resend in voice mode.
// - `commit()` closes the turn: its frames go out, then `commit{turn,
//   lastSeq}`, and the promise resolves with the server's `committed{turn}`.
//   The session stays open. A next turn may begin before that answer; its
//   frames queue behind the commit.
// - `keepalive` goes every 10 s while the session is open, so the 30 s idle
//   close never fires in voice mode.
// - The frames of turns not yet committed are kept in memory (≤ 60 s) and
//   dropped at `committed`. After an unexpected close the session is
//   re-minted with `replayOf` and only those frames are replayed, flagged
//   REPLAY, at ≤ 4× real time; committed turns are never replayed. A close
//   between turns re-mints lazily, when the next turn begins.
// - `closed{replaced}` (voice or dictation started elsewhere) is terminal and
//   never re-minted here: the controller pauses voice mode.
// - Rollover: at a turn boundary past `maxSessionSec − 60 s` the next turn
//   opens on a new session; the old one gets `stop` once the new one is ready.
// - A server whose `ready.capabilities.commit` is not true gets one session
//   per turn: `stop` ends the turn and the next turn mints again.
// Nothing here reads transcript text: `committed` is handed on as it came.
import { wsUrl } from '@/lib/shell';
import type {
  PcmFrame,
  SpeechMintRequest,
  SpeechMintResponse,
  SpeechSocketApi,
  SpeechSocketOptions,
} from '@/speech/contracts';
import { SpeechHttpError } from '@/speech/contracts';
import type { SpeechConversation } from '@/speech/conversation';
import { speechIssue, speechIssueFor, terminalCloseCode, type SpeechIssue } from '@/speech/errors';
import {
  CLOSE_CODES,
  FLAG_REPLAY,
  FRAME_MS,
  RECONNECT_BACKOFF_MS,
  RECONNECT_WINDOW_MS,
  REPLAY_MIN_FRAME_INTERVAL_MS,
  VOICE_KEEPALIVE_MS,
  type CommittedMessage,
  type ServerMessage,
} from '@/speech/transport/protocol';
import type { VoiceStyle } from './contracts';
import { OPEN_TURN_RING_MS, ROLLOVER_MARGIN_SEC, VOICE_COMMIT_TIMEOUT_MS } from './policy';

export type VoiceTimers = {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
};

export type VoiceLinkDeps = {
  mint(request: SpeechMintRequest): Promise<SpeechMintResponse>;
  openSocket(options: SpeechSocketOptions): SpeechSocketApi;
  timers: VoiceTimers;
  now(): number;
  online(): boolean;
  /** Re-read on turn boundaries; the server loads the stored preference at mint. */
  endpointDelayMs?(): number;
  /** Read only at a safe turn boundary; the server owns language selection. */
  conversation?(): SpeechConversation;
};

export type VoiceReady = {
  sessionId: string;
  provider: string;
  /** False: one session per turn (an older server). */
  commit: boolean;
};

/** The answering session stays attached even if it retires or rolls over before delivery. */
export type VoiceCommitted = CommittedMessage & { sessionId: string };

export type VoiceLinkEvents = {
  onReady(ready: VoiceReady): void;
  onPartial(key: string, message: Extract<ServerMessage, { type: 'partial' }>): void;
  onFinal(key: string, message: Extract<ServerMessage, { type: 'final' }>): void;
  /** Hands-free: the provider's end of the open turn (advisory). */
  onEndpoint(message: Extract<ServerMessage, { type: 'endpoint' }>): void;
  onDegraded(message: Extract<ServerMessage, { type: 'degraded' }>): void;
  /** The session closed with a turn open and is being re-minted. */
  onReconnecting(): void;
  /** Voice or dictation started elsewhere: pause, never re-mint here. */
  onReplaced(): void;
  /**
   * Terminal: the session could not be kept or re-opened. `cause` is the
   * mint's error when a mint was refused (its 503 reason picks the copy).
   */
  onFailure(issue: SpeechIssue, cause?: unknown): void;
};

type Queued = { frame: PcmFrame; turn: number; flags: number };

type Commit = {
  turn: number;
  /** The last frame of the turn, or -1 when it had none. */
  lastSeq: number;
  sent: boolean;
  resolve(message: VoiceCommitted): void;
  reject(issue: SpeechIssue): void;
  timer: unknown;
};

type Session = {
  socket: SpeechSocketApi | null;
  mint: SpeechMintResponse | null;
  ready: VoiceReady | null;
  openedAt: number;
  endpointDelayMs: number | undefined;
  contextKey: string;
  /** The last frame sent on this session, for `stop` at a rollover. */
  lastSeq: number;
  /** A fatal `error` code the server sent before closing. */
  fatal: string | null;
  /** One session per turn: the turn this session serves. */
  turn: number;
};

/** Mint answers that another attempt cannot change. */
const TERMINAL_MINT_STATUSES: ReadonlySet<number> = new Set([400, 401, 402, 403, 415, 429, 503]);
const BACKPRESSURE_RETRY_MS = 50;
/** How long a rolled-over session may take to answer its `stop`. */
const RETIRE_TIMEOUT_MS = 5_000;

export class VoiceLink {
  private session: Session | null = null;
  private retiring: Session | null = null;
  private connecting = false;
  /** The open turn (frames pushed now belong to it) or the next one. */
  private turn = 0;
  private turnOpen = false;
  /** Frames of turns not yet committed, oldest first. */
  private ring: Queued[] = [];
  /** Frames not yet sent on the current session, in order. */
  private queue: Queued[] = [];
  private commits: Commit[] = [];
  private keepaliveTimer: unknown = null;
  private retryTimer: unknown = null;
  private closeStart: number | null = null;
  private attempt = 0;
  private done = false;
  private rolling = false;
  private lastSessionId: string | null = null;
  private sessions = 0;
  private pumping = false;
  private lastSendAt = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly style: VoiceStyle,
    private readonly deps: VoiceLinkDeps,
    private readonly events: VoiceLinkEvents,
  ) {}

  /** The current server session id (the turn body's `sessionId`, and G8). */
  get sessionId(): string | null {
    return this.session?.ready?.sessionId ?? null;
  }

  get openTurn(): number | null {
    return this.turnOpen ? this.turn : null;
  }

  /** Mint and connect now (voice mode entry, inside the gesture). */
  open(): void {
    this.ensureSession();
  }

  /** Start a turn: the frames pushed from now on belong to it. */
  beginTurn(): number {
    if (!this.turnOpen && !this.done) {
      const session = this.session;
      if (
        session?.ready &&
        !this.commits.length &&
        (session.endpointDelayMs !== this.deps.endpointDelayMs?.() || this.languageChanged(session))
      )
        this.maybeRollover(session, true);
      this.turnOpen = true;
      this.ensureSession();
    }
    return this.turn;
  }

  /** A captured frame. Sent only while a turn is open; otherwise dropped here. */
  push(frame: PcmFrame): void {
    if (!this.turnOpen || this.done) return;
    const item: Queued = { frame, turn: this.turn, flags: 0 };
    this.ring.push(item);
    const keep = Math.ceil(OPEN_TURN_RING_MS / FRAME_MS);
    if (this.ring.length > keep) this.ring.splice(0, this.ring.length - keep);
    this.queue.push(item);
    void this.pump();
  }

  /** Close the open turn: resolves with its `committed`. */
  commit(): Promise<VoiceCommitted> {
    if (this.done) return Promise.reject(speechIssue('internal'));
    const turn = this.turn;
    const lastSeq = this.ring.filter((item) => item.turn === turn).at(-1)?.frame.seq ?? -1;
    this.turnOpen = false;
    this.turn += 1;
    const committed = new Promise<VoiceCommitted>((resolve, reject) => {
      const timer = this.deps.timers.setTimeout(
        () => this.commitTimedOut(turn),
        VOICE_COMMIT_TIMEOUT_MS,
      );
      this.commits.push({ turn, lastSeq, sent: false, resolve, reject, timer });
    });
    this.ensureSession();
    void this.pump();
    return committed;
  }

  /** Voice mode is leaving: drop the audio server-side and close every session. */
  close(issue: SpeechIssue = speechIssue('cancelled')): void {
    if (this.done) return;
    this.done = true;
    this.session?.socket?.send({ type: 'cancel' });
    this.teardown(this.session);
    this.teardown(this.retiring);
    this.session = null;
    this.retiring = null;
    this.clearTimer('retryTimer');
    this.clearTimer('keepaliveTimer');
    for (const commit of this.commits.splice(0)) {
      this.deps.timers.clearTimeout(commit.timer);
      commit.reject(issue);
    }
    this.ring = [];
    this.queue = [];
  }

  // ── Connection ────────────────────────────────────────────────────────────

  /** Mint a session when a turn needs one and none is open or on its way. */
  private ensureSession() {
    if (this.done || this.session || this.connecting || this.retryTimer !== null) return;
    void this.connect();
  }

  private languageChanged(session: Session): boolean {
    const languages = this.deps.conversation?.().languages;
    return (
      languages != null && session.contextKey !== JSON.stringify([...new Set(languages)].sort())
    );
  }

  private async connect() {
    this.connecting = true;
    // The predecessor also carries the server's turn-number lineage when
    // no audio remains to replay (idle reconnects and between-turn rollover).
    const replayOf = this.lastSessionId;
    const endpointDelayMs = this.deps.endpointDelayMs?.();
    const conversation = this.deps.conversation?.();
    let minted: SpeechMintResponse;
    try {
      minted = await this.deps.mint({
        mode: 'voice',
        style: this.style,
        ...(conversation?.threadId ? { threadId: conversation.threadId } : {}),
        ...(replayOf ? { replayOf } : {}),
      });
    } catch (error) {
      this.connecting = false;
      if (!this.done) this.mintFailed(error);
      return;
    }
    this.connecting = false;
    if (this.done) return;
    const session: Session = {
      socket: null,
      mint: minted,
      ready: null,
      openedAt: this.deps.now(),
      endpointDelayMs,
      contextKey: JSON.stringify([...new Set(minted.languages.codes)].sort()),
      lastSeq: -1,
      fatal: null,
      turn: this.commits[0]?.turn ?? this.turn,
    };
    this.session = session;
    session.socket = this.deps.openSocket({
      url: `${wsUrl(minted.wsPath)}?ticket=${encodeURIComponent(minted.ticket)}`,
      onMessage: (message) => this.onMessage(session, message),
      onClose: (code) => this.onClose(session, code),
    });
  }

  private mintFailed(error: unknown) {
    if (error instanceof SpeechHttpError && TERMINAL_MINT_STATUSES.has(error.status))
      return this.fail(speechIssueFor(error), error);
    this.scheduleRetry();
  }

  private scheduleRetry() {
    if (this.done) return;
    this.closeStart ??= this.deps.now();
    const remaining = RECONNECT_WINDOW_MS - (this.deps.now() - this.closeStart);
    if (remaining <= 0) return this.fail(speechIssue(this.deps.online() ? 'timeout' : 'offline'));
    this.events.onReconnecting();
    const backoff = RECONNECT_BACKOFF_MS[Math.min(this.attempt, RECONNECT_BACKOFF_MS.length - 1)];
    this.attempt += 1;
    this.clearTimer('retryTimer');
    this.retryTimer = this.deps.timers.setTimeout(
      () => {
        this.retryTimer = null;
        this.ensureSession();
      },
      Math.min(remaining, backoff ?? 8_000),
    );
  }

  // ── Server messages ─────────────────────────────────────────────────────

  private readonly handlers: {
    [K in ServerMessage['type']]: (
      session: Session,
      message: Extract<ServerMessage, { type: K }>,
    ) => void;
  } = {
    ready: (session, message) => this.onReady(session, message),
    ack: () => {},
    partial: (_session, message) => this.events.onPartial(this.key(message.seg), message),
    final: (_session, message) => this.events.onFinal(this.key(message.seg), message),
    committed: (session, message) => this.onCommitted(session, message),
    // Voice mode never replays on request (C2-4 "Gaps").
    resend: () => {},
    degraded: (_session, message) => this.events.onDegraded(message),
    error: (session, message) => {
      if (message.fatal) session.fatal = message.code;
    },
    closed: (_session, message) => {
      if (message.reason === 'replaced') this.replaced();
    },
    endpoint: (_session, message) => {
      if (this.turnOpen && message.turn === this.turn) this.events.onEndpoint(message);
    },
  };

  private onMessage(session: Session, message: ServerMessage) {
    if (this.done || session !== this.session) return;
    (this.handlers[message.type] as (s: Session, m: ServerMessage) => void)(session, message);
  }

  /** Partials and finals are keyed per session, so a replay never merges into an old key. */
  private key(seg: number | string): string {
    return `${this.sessions}:${seg}`;
  }

  private onReady(session: Session, message: Extract<ServerMessage, { type: 'ready' }>) {
    session.ready = {
      sessionId: message.sessionId,
      provider: message.provider.id,
      commit: message.capabilities.commit === true,
    };
    this.sessions += 1;
    this.lastSessionId = session.ready.sessionId;
    this.closeStart = null;
    this.attempt = 0;
    this.finishRollover();
    this.startKeepalive();
    // Everything not yet committed goes (again) to this session: frames an
    // earlier session already had are a replay.
    const unsent = new Set(this.queue);
    this.queue = this.ring.map((item) =>
      unsent.has(item) ? item : { ...item, flags: FLAG_REPLAY },
    );
    for (const commit of this.commits) commit.sent = false;
    this.events.onReady(session.ready);
    void this.pump();
  }

  private onCommitted(session: Session, message: CommittedMessage) {
    const commit = this.commits[0];
    if (!commit || !commit.sent) return;
    if (message.turn !== undefined && message.turn !== commit.turn) return;
    const sessionId = session.ready?.sessionId;
    if (!sessionId) return this.fail(speechIssue('internal'));
    this.commits.shift();
    this.deps.timers.clearTimeout(commit.timer);
    this.ring = this.ring.filter((item) => item.turn > commit.turn);
    this.queue = this.queue.filter((item) => item.turn > commit.turn);
    // Without `commit` support the session ended with the turn.
    if (!session.ready?.commit) this.retire(session);
    else this.maybeRollover(session);
    commit.resolve({ ...message, sessionId });
    if (this.turnOpen || this.queue.length > 0 || this.commits.length > 0) this.ensureSession();
    void this.pump();
  }

  private onClose(session: Session, code: number) {
    if (session === this.retiring) {
      this.retiring = null;
      return;
    }
    if (session !== this.session || this.done) return;
    session.socket = null;
    this.session = null;
    this.clearTimer('keepaliveTimer');
    const idle = !this.turnOpen && this.commits.length === 0;
    // Between turns nothing is lost: the next turn mints again.
    if (idle && (code === CLOSE_CODES.idle || !session.ready?.commit)) return;
    const terminal = session.fatal ?? (code === CLOSE_CODES.idle ? null : terminalCloseCode(code));
    if (terminal) return this.fail(speechIssue(terminal));
    if (idle) return;
    this.scheduleRetry();
  }

  private replaced() {
    const issue = speechIssue('replaced');
    this.close(issue);
    this.events.onReplaced();
  }

  // ── Rollover ────────────────────────────────────────────────────────────

  private maybeRollover(session: Session, preferencesChanged = false) {
    const cap = session.mint?.voice?.maxSessionSec ?? session.mint?.limits.maxSessionSec ?? 900;
    const age = (this.deps.now() - session.openedAt) / 1000;
    if (this.rolling || (!preferencesChanged && age < cap - ROLLOVER_MARGIN_SEC)) return;
    this.rolling = true;
    this.retiring = session;
    this.session = null;
    this.clearTimer('keepaliveTimer');
    this.ensureSession();
  }

  private finishRollover() {
    if (!this.rolling) return;
    this.rolling = false;
    const old = this.retiring;
    if (!old) return;
    old.socket?.send({ type: 'stop', lastSeq: old.lastSeq });
    // The server answers `stop` and closes; close it ourselves if it does not.
    this.deps.timers.setTimeout(() => {
      if (this.retiring !== old) return;
      this.teardown(old);
      this.retiring = null;
    }, RETIRE_TIMEOUT_MS);
  }

  private retire(session: Session) {
    this.teardown(session);
    if (this.session === session) this.session = null;
  }

  // ── Sending ─────────────────────────────────────────────────────────────

  private async pump() {
    if (this.pumping) return;
    this.pumping = true;
    try {
      while (await this.step());
    } finally {
      this.pumping = false;
    }
  }

  /** One send: a due commit, or the next frame. False when nothing can go now. */
  private async step(): Promise<boolean> {
    const session = this.session;
    if (!session?.ready || !session.socket || this.done) return false;
    const next = this.queue[0];
    const commit = this.commits.find((item) => !item.sent);
    if (commit && (!next || next.turn > commit.turn)) return this.sendCommit(session, commit);
    if (!next) return false;
    // One session per turn: a later turn waits for its own session.
    if (!session.ready.commit && next.turn !== session.turn) return false;
    if (next.flags & FLAG_REPLAY) await this.pace();
    if (this.session !== session || !session.socket || this.queue[0] !== next) return true;
    if (!session.socket.sendFrame(next.frame, next.flags)) {
      await this.sleep(BACKPRESSURE_RETRY_MS);
      return true;
    }
    this.lastSendAt = this.deps.now();
    session.lastSeq = next.frame.seq;
    this.queue.shift();
    return true;
  }

  private sendCommit(session: Session, commit: Commit): boolean {
    if (!session.ready?.commit && session.turn !== commit.turn) return false;
    commit.sent = true;
    session.socket?.send(
      session.ready?.commit
        ? { type: 'commit', turn: commit.turn, lastSeq: commit.lastSeq }
        : { type: 'stop', lastSeq: commit.lastSeq },
    );
    return true;
  }

  private pace(): Promise<void> {
    const wait = this.lastSendAt + REPLAY_MIN_FRAME_INTERVAL_MS - this.deps.now();
    return wait > 0 ? this.sleep(wait) : Promise.resolve();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => this.deps.timers.setTimeout(resolve, ms));
  }

  // ── Endings ─────────────────────────────────────────────────────────────

  private commitTimedOut(turn: number) {
    if (this.commits.some((commit) => commit.turn === turn)) this.fail(speechIssue('timeout'));
  }

  private fail(issue: SpeechIssue, cause?: unknown) {
    if (this.done) return;
    this.close(issue);
    this.events.onFailure(issue, cause);
  }

  private startKeepalive() {
    this.clearTimer('keepaliveTimer');
    const tick = () => {
      this.keepaliveTimer = null;
      const session = this.session;
      if (this.done || !session?.ready?.commit) return;
      session.socket?.send({ type: 'keepalive' });
      this.keepaliveTimer = this.deps.timers.setTimeout(tick, VOICE_KEEPALIVE_MS);
    };
    this.keepaliveTimer = this.deps.timers.setTimeout(tick, VOICE_KEEPALIVE_MS);
  }

  private teardown(session: Session | null) {
    if (!session) return;
    const socket = session.socket;
    session.socket = null;
    socket?.close();
  }

  private clearTimer(name: 'retryTimer' | 'keepaliveTimer') {
    if (this[name] !== null) this.deps.timers.clearTimeout(this[name]);
    this[name] = null;
  }
}
